import { useMemo, useState, Fragment } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SpecificImportHistoryPanel from "@/pages/SpecificImportHistoryPanel";
import {
  FileUp, Download, AlertCircle, CheckCircle2, ChevronDown, ChevronRight,
  Info, Eraser, Ban, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

function ActionBadge({ acao }) {
  if (acao === "update") return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">Alterar</Badge>;
  if (acao === "clear") return <Badge className="bg-amber-100 text-amber-900 border-amber-200 border"><Eraser className="h-3 w-3 mr-1" />Limpar</Badge>;
  if (acao === "no_change") return <Badge variant="outline" className="text-muted-foreground">Sem alteração</Badge>;
  if (acao === "error") return <Badge className="bg-red-100 text-red-800 border-red-200 border">Erro</Badge>;
  return null;
}

function RowDetails({ item }) {
  if (!item.alteracoes?.length && !item.erros?.length) {
    return <div className="text-[12px] text-muted-foreground italic px-3 py-2">Nenhuma coluna preenchida — nada será alterado nesta linha.</div>;
  }
  return (
    <div className="px-3 py-2 bg-[#F9F6EF]/50">
      {item.erros?.length > 0 && (
        <div className="mb-2 text-[12px] text-red-700 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <div>{item.erros.join(" · ")}</div>
        </div>
      )}
      {item.alteracoes?.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[24%]">Campo</TableHead>
              <TableHead className="w-[30%]">Valor atual</TableHead>
              <TableHead className="w-[30%]">Novo valor</TableHead>
              <TableHead className="w-[16%]">Ação</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {item.alteracoes.map((a) => (
              <TableRow key={a.label}>
                <TableCell className="text-[12.5px] font-medium">{a.label}</TableCell>
                <TableCell className="text-[12.5px] font-mono text-muted-foreground">
                  {a.valor_atual === "" ? <span className="italic">(vazio)</span> : a.valor_atual}
                </TableCell>
                <TableCell className="text-[12.5px] font-mono">
                  {a.acao === "clear"
                    ? <span className="italic text-amber-800">(limpar)</span>
                    : (a.valor_novo === "" ? <span className="italic">(vazio)</span> : a.valor_novo)}
                </TableCell>
                <TableCell>
                  <ActionBadge acao={a.acao} />
                  {a.erro && <div className="text-[11px] text-red-600 mt-1">{a.erro}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

export default function SpecificImportPage() {
  const { companies, companyParam } = useCompany();
  const [company, setCompany] = useState(companyParam || companies[0]?.id || "");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [filter, setFilter] = useState("all"); // all | changes | errors | nochange
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/employees/specific-import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement("a");
      link.href = url; link.download = "modelo_atualizacao_especifica.xlsx"; link.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Falha ao baixar modelo"); }
  };

  const upload = async () => {
    if (!file || !company) { toast.error("Selecione arquivo e empresa"); return; }
    setLoading(true); setPreview(null); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("rh_token");
      const resp = await fetch(`${API_BASE}/employees/specific-import/preview?company_id=${company}`, {
        method: "POST", body: fd,
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(formatApiError(err?.detail) || "Falha ao processar");
      }
      const data = await resp.json();
      setPreview(data);
      // Expande erros por padrão para o usuário revisar
      const exp = {};
      (data.itens || []).forEach((it) => { if (!it.valido || it.num_alteracoes > 0) exp[it.linha] = true; });
      setExpanded(exp);
    } catch (ex) { toast.error(ex.message); }
    finally { setLoading(false); }
  };

  const cancel = async () => {
    if (!preview) return;
    try {
      await api.post("/employees/specific-import/cancel", { batch_id: preview.batch_id });
    } catch { /* ignore */ }
    setPreview(null); setFile(null); setExpanded({});
    toast.success("Importação cancelada");
  };

  const commit = async () => {
    if (!preview) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/employees/specific-import/commit", { batch_id: preview.batch_id });
      setConfirmOpen(false);
      setResult(data);
      setPreview(null);
      toast.success(`${data.atualizados} colaborador(es) atualizados · ${data.campos_alterados_total} campo(s)`);
    } catch (ex) {
      toast.error(formatApiError(ex.response?.data?.detail) || "Falha ao importar");
    } finally { setCommitting(false); }
  };

  const filtered = useMemo(() => {
    if (!preview) return [];
    return preview.itens.filter((it) => {
      if (filter === "all") return true;
      if (filter === "errors") return !it.valido;
      if (filter === "changes") return it.valido && it.num_alteracoes > 0;
      if (filter === "nochange") return it.valido && it.num_alteracoes === 0;
      return true;
    });
  }, [preview, filter]);

  return (
    <div className="space-y-6" data-testid="specific-import-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#8A6B2B] font-medium">Atualização em lote</div>
          <h1 className="font-display text-3xl font-semibold">Importação Específica · Cadastro</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground max-w-3xl">
            Atualize seletivamente campos de colaboradores <b>já existentes</b>. Célula em branco = <b>não altera</b>.
            Escreva <code className="bg-[#F1E9CF] px-1.5 py-0.5 rounded text-[12px] font-mono">[LIMPAR]</code> para apagar o valor de um campo.
          </p>
        </div>
        <Button variant="outline" onClick={downloadTemplate} data-testid="specific-import-template-btn">
          <Download className="h-4 w-4 mr-2" /> Baixar modelo
        </Button>
      </div>

      <Tabs defaultValue="new" className="w-full">
        <TabsList data-testid="specific-import-tabs">
          <TabsTrigger value="new" data-testid="tab-specific-new">Nova importação</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-specific-history">Histórico &amp; Desfazer</TabsTrigger>
        </TabsList>

        <TabsContent value="new" className="space-y-6 mt-4">

      <div className="rounded-lg border border-border bg-white p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Empresa destino</label>
            <Select value={company} onValueChange={setCompany}>
              <SelectTrigger data-testid="specific-import-company"><SelectValue placeholder="Selecione" /></SelectTrigger>
              <SelectContent>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">Arquivo (.xlsx / .csv)</label>
            <input
              type="file"
              accept=".xlsx,.csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="specific-import-file-input"
              className="block w-full text-sm text-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-[#234935] file:text-white file:text-xs file:font-medium hover:file:bg-[#2c5c43] cursor-pointer"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={upload} disabled={!file || !company || loading}
              className="bg-[#234935] hover:bg-[#2c5c43] w-full" data-testid="specific-import-upload-btn">
              <FileUp className="h-4 w-4 mr-2" /> {loading ? "Analisando..." : "Analisar prévia"}
            </Button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
          <div className="rounded-md border border-[#E8D9AE] bg-[#F9F4E7] px-3 py-2 text-[#5C4320] flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#8A6B2B]" />
            <span><b>Matrícula</b> é obrigatória e usada como identificador. Cargos não permitidos são ignorados. Colaboradores <b>não são criados</b> por este fluxo.</span>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900 flex items-start gap-2">
            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>Auditoria detalhada é gerada por campo (usuário, matrícula, valor anterior, novo valor, data/hora).</span>
          </div>
        </div>
      </div>

      {preview && (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap text-[13px]">
              <span className="font-display text-[15px] font-semibold">Prévia · {preview.filename}</span>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                <CheckCircle2 className="h-3 w-3 mr-1" /> {preview.total_com_alteracoes} p/ atualizar
              </Badge>
              <Badge variant="outline" className="bg-slate-50 text-slate-700 border-slate-200">
                {preview.total_sem_alteracoes} sem alteração
              </Badge>
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                <AlertCircle className="h-3 w-3 mr-1" /> {preview.total_com_erro} com erro
              </Badge>
              <Badge variant="outline" className="bg-[#F1E9CF] text-[#5C4320] border-[#E8D9AE]">
                {preview.total_campos_alterados} campo(s)
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="h-9 w-[180px] text-[12.5px]" data-testid="specific-import-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as linhas</SelectItem>
                  <SelectItem value="changes">Apenas com alterações</SelectItem>
                  <SelectItem value="errors">Apenas com erro</SelectItem>
                  <SelectItem value="nochange">Sem alteração</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={cancel} data-testid="specific-import-cancel-btn">
                <Ban className="h-4 w-4 mr-1" /> Cancelar
              </Button>
              <Button
                onClick={() => setConfirmOpen(true)}
                disabled={preview.total_com_alteracoes === 0}
                className="bg-[#234935] hover:bg-[#2c5c43]"
                data-testid="specific-import-commit-btn"
              >
                Confirmar importação
              </Button>
            </div>
          </div>

          {(preview.colunas_desconhecidas?.length > 0 || preview.colunas_proibidas?.length > 0) && (
            <div className="px-5 py-3 border-b border-border bg-amber-50 text-[12px] text-amber-900">
              {preview.colunas_desconhecidas?.length > 0 && (
                <div><b>Colunas ignoradas:</b> {preview.colunas_desconhecidas.join(", ")}</div>
              )}
              {preview.colunas_proibidas?.length > 0 && (
                <div><b>Colunas proibidas (ignoradas):</b> {preview.colunas_proibidas.join(", ")}</div>
              )}
            </div>
          )}

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[64px]"></TableHead>
                <TableHead className="w-[80px]">Linha</TableHead>
                <TableHead className="w-[160px]">Matrícula</TableHead>
                <TableHead>Nome atual</TableHead>
                <TableHead className="w-[130px]">Alterações</TableHead>
                <TableHead className="w-[120px]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((it) => {
                const isOpen = expanded[it.linha];
                return (
                  <Fragment key={it.linha}>
                    <TableRow className="cursor-pointer hover:bg-slate-50/60"
                      onClick={() => setExpanded((e) => ({ ...e, [it.linha]: !e[it.linha] }))}
                      data-testid={`specific-import-row-${it.linha}`}
                    >
                      <TableCell>
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">{it.linha}</TableCell>
                      <TableCell className="font-mono text-[12px]">{it.matricula || <span className="italic text-red-700">(vazia)</span>}</TableCell>
                      <TableCell className="text-[13px]">{it.nome_atual || <span className="italic text-muted-foreground">—</span>}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{it.num_alteracoes}</Badge>
                      </TableCell>
                      <TableCell>
                        {it.valido
                          ? (it.num_alteracoes > 0
                              ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">Pronto</Badge>
                              : <Badge variant="outline" className="text-muted-foreground">Nada a alterar</Badge>)
                          : <Badge className="bg-red-100 text-red-800 border-red-200 border" title={it.erros.join("; ")}>Erro</Badge>
                        }
                      </TableCell>
                    </TableRow>
                    {isOpen && (
                      <TableRow className="bg-[#F9F6EF]/30">
                        <TableCell colSpan={6} className="p-0">
                          <RowDetails item={it} />
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-5" data-testid="specific-import-result">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            <div className="font-display text-[15px] font-semibold text-emerald-900">Importação concluída</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-[13px]">
            <div className="rounded-md bg-white border border-emerald-200 px-3 py-2">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Colaboradores</div>
              <div className="text-[18px] font-semibold text-emerald-900">{result.atualizados}</div>
            </div>
            <div className="rounded-md bg-white border border-emerald-200 px-3 py-2">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Campos alterados</div>
              <div className="text-[18px] font-semibold text-emerald-900">{result.campos_alterados_total}</div>
            </div>
            <div className="rounded-md bg-white border border-slate-200 px-3 py-2">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Sem alteração</div>
              <div className="text-[18px] font-semibold">{result.linhas_sem_alteracao}</div>
            </div>
            <div className="rounded-md bg-white border border-red-200 px-3 py-2">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Linhas com erro</div>
              <div className="text-[18px] font-semibold text-red-700">{result.linhas_com_erro?.length || 0}</div>
            </div>
          </div>
          {result.linhas_com_erro?.length > 0 && (
            <div className="mt-2">
              <div className="text-[12.5px] font-medium text-red-800 mb-1">Linhas ignoradas</div>
              <div className="text-[12px] space-y-0.5">
                {result.linhas_com_erro.map((l) => (
                  <div key={l.linha} className="font-mono">L{l.linha} · {l.matricula || "(sem matrícula)"} — {l.erros?.join("; ")}</div>
                ))}
              </div>
            </div>
          )}
          {result.detalhes_auditoria?.length > 0 && (
            <details className="mt-4">
              <summary className="cursor-pointer text-[12.5px] text-emerald-900 font-medium">
                Ver detalhes de auditoria ({result.detalhes_auditoria.length} alteração{result.detalhes_auditoria.length === 1 ? "" : "es"})
              </summary>
              <div className="mt-2 overflow-auto max-h-80 border border-emerald-200 rounded-md bg-white">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Matrícula</TableHead>
                      <TableHead>Campo</TableHead>
                      <TableHead>Anterior</TableHead>
                      <TableHead>Novo</TableHead>
                      <TableHead>Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.detalhes_auditoria.map((d, i) => (
                      <TableRow key={i}>
                        <TableCell className="font-mono text-[12px]">{d.matricula}</TableCell>
                        <TableCell className="text-[12.5px]">{d.label}</TableCell>
                        <TableCell className="font-mono text-[11.5px] text-muted-foreground">{d.valor_anterior || <span className="italic">(vazio)</span>}</TableCell>
                        <TableCell className="font-mono text-[11.5px]">{d.valor_novo || <span className="italic">(vazio)</span>}</TableCell>
                        <TableCell><ActionBadge acao={d.acao} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </details>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="specific-import-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar importação específica?</DialogTitle>
            <DialogDescription>
              Você vai atualizar <b>{preview?.total_com_alteracoes}</b> colaborador(es) com{" "}
              <b>{preview?.total_campos_alterados}</b> alteração(ões) de campos. Linhas com erro serão ignoradas.
              Após o commit, um snapshot reversível por campo é gerado — você pode desfazer no histórico.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={committing}>Voltar</Button>
            <Button
              onClick={commit}
              disabled={committing}
              className="bg-[#234935] hover:bg-[#2c5c43]"
              data-testid="specific-import-confirm-btn"
            >
              {committing ? "Importando..." : "Sim, importar agora"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <SpecificImportHistoryPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
